import React from 'react';
import { Skeleton } from '@design-system/Skeleton';
import { Image } from '@design-system/Image';
import { isHexColor } from '@utils/checkHexCode';
import { ProgressBar } from '@design-system/ProgressBar';
import { AboutSection } from '@screens/LevelTwoDetail/AboutSection';
import { Trends } from '@design-system/Trends';
import { transformTrendToVGraph } from '@/utils/health/transformVGraph';
import { cn } from '@/utils/styles';
import {
  CommonlyLinkedSymptoms,
  IScoreDetailResponse,
} from '@viome/viome-base/src/usecase/Health/Health.model';
import { useGetScoreReference } from '@viome/viome-base/src/usecase/Health/HealthQuery.ts';
import { HealthReferenceSection } from '@screens/HealthPages/HealthReferenceSection';
import { getL2ImageUrl } from '@utils/nutrition/getImageUrl';

interface HeaderSectionProps {
  isLoading: boolean;
  l2Score: IScoreDetailResponse;
  commonlyLinkedSymptoms?: CommonlyLinkedSymptoms;
  scoreId: number;
}

export const L2HeaderSection: React.FC<HeaderSectionProps> = ({
  l2Score,
  isLoading,
  commonlyLinkedSymptoms,
  scoreId,
}) => {
  const {
    about,
    image_url: imageUrl,
    rank,
    rank_color: rankColor,
    score,
    title,
    trend,
  } = l2Score;

  const { data: referenceData } = useGetScoreReference(scoreId);

  return (
    <div className="md:flex flex-col bg-transparent">
      {/* Header Section */}

      <div className="-mt-8 md:mt-12 md:flex md:flex-row-reverse items-center justify-between">
        {isLoading ? (
          <Skeleton className="h-52 w-52 rounded-full mb-6 min-w-56" />
        ) : (
          <div className="flex items-center justify-center md:justify-end overflow-hidden min-w-60">
            <Image
              src={getL2ImageUrl(imageUrl)}
              alt={title}
              className="h-[13.75rem] w-[13.75rem] md:h-[15.25rem] md:w-[15.25rem] flex items-center justify-center overflow-hidden"
            />
          </div>
        )}

        <div className="flex flex-col w-full">
          <div className="flex lg:flex-col items-center md:items-start justify-between">
            <div>
              {isLoading ? (
                <Skeleton className="h-8 w-72 mb-2" />
              ) : (
                <h1 className="lg:typography-d1 typography-d2 w-[90%] md:w-full">
                  {title}
                </h1>
              )}
              {isLoading ? (
                <Skeleton className="h-5 w-40 mb-6" />
              ) : (
                <p
                  className={cn(
                    'typography-body text-left',
                    !isHexColor(rankColor) && rankColor,
                  )}
                  style={
                    isHexColor(rankColor) ? { color: rankColor } : undefined
                  }
                >
                  {rank}
                </p>
              )}
            </div>
            {isLoading ? (
              <Skeleton className="h-12 w-16 mb-4" />
            ) : (
              <h2 className="lg:typography-num-large typography-d1 mb-1 md:mt-8 sm:mt-0">
                {score}
              </h2>
            )}
          </div>
          <div className="text-primary w-full">
            <div className="w-full typography-footnote text-tertiary mt-3 ">
              {isLoading ? (
                <Skeleton className="h-2 w-full rounded-full" />
              ) : (
                <ProgressBar progress={score} color={rankColor} />
              )}
              <div className="flex items-center justify-between mt-3">
                <p>0</p>
                <div className="flex items-center justify-between">
                  <HealthReferenceSection scoreId={scoreId} />
                </div>
                <p>100</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {trend && trend.length > 1 && (
        <div className="mt-12">
          {isLoading ? (
            <Skeleton className="h-32 w-full rounded-lg" />
          ) : (
            <div>
              <Trends
                trend={transformTrendToVGraph(trend) || []}
                referenceData={referenceData}
                topIndicator="100"
                bottomIndicator="0"
              />
            </div>
          )}
        </div>
      )}

      {/* About Section */}
      <AboutSection
        title={title}
        about={about}
        symptoms={commonlyLinkedSymptoms}
        isLoading={isLoading}
      />
    </div>
  );
};
